import React, { useState } from "react";
import { motion } from "framer-motion";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Icon } from "@/components/Icon";

export default function Streamer() {
  const { user, refresh, streamerMode, setStreamerMode } = useAuth();
  const [toUser, setToUser] = useState("");
  const [amount, setAmount] = useState("");
  const [cur, setCur] = useState("coins");
  const [busy, setBusy] = useState(false);

  const tip = async () => {
    const amt = parseFloat(amount);
    if (!toUser.trim()) { toast.error("Enter a follower's username"); return; }
    if (!amt || amt <= 0) { toast.error("Enter a valid amount"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/tips", { to_username: toUser.trim(), amount: amt, currency: cur });
      toast.success(`Tipped ${data.amount.toFixed(2)} ${data.currency} to ${data.to}!`);
      setToUser(""); setAmount("");
      await refresh();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <div className="mx-auto max-w-3xl px-4 py-8 sm:px-6">
      <p className="mb-1 text-xs uppercase tracking-[0.2em] text-[#a1a1aa]">Creators</p>
      <h1 className="mb-8 flex items-center gap-3 font-display text-4xl font-black tracking-tighter sm:text-5xl">
        <Icon name="broadcast" size={32} className="text-[#00D632]" /> Streamer Mode
      </h1>

      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
        className="mb-6 flex items-center justify-between rounded-2xl border border-[#27272A] bg-[#141417] p-6" data-testid="streamer-toggle-card">
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#27272A] text-white"><Icon name="lock" size={20} /></div>
          <div>
            <h2 className="font-display text-xl font-bold tracking-tight">Hide my balances</h2>
            <p className="text-sm text-[#a1a1aa]">Masks your coins & cash across the app while you're live on stream.</p>
          </div>
        </div>
        <Switch checked={streamerMode} onCheckedChange={setStreamerMode} data-testid="streamer-switch" />
      </motion.div>

      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}
        className="rounded-2xl border border-[#00D632]/40 bg-[#00D632]/5 p-6">
        <div className="mb-4 flex items-center gap-2">
          <Icon name="promotions" size={20} className="text-[#00D632]" />
          <h2 className="font-display text-xl font-bold tracking-tight">Tip a follower</h2>
        </div>
        <div className="space-y-4">
          <div>
            <Label>Follower username</Label>
            <Input value={toUser} data-testid="tip-username" onChange={(e) => setToUser(e.target.value)} placeholder="their exact username" className="mt-1.5" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Amount</Label>
              <Input type="number" min="0" step="0.01" value={amount} data-testid="tip-amount" onChange={(e) => setAmount(e.target.value)} placeholder="0.00" className="mt-1.5" />
            </div>
            <div>
              <Label>From balance</Label>
              <div className="mt-1.5 grid grid-cols-2 gap-2">
                <button onClick={() => setCur("coins")} data-testid="tip-cur-coins"
                  className={`rounded-lg border px-2 py-2 text-xs font-bold ${cur === "coins" ? "border-[#00D632] bg-[#00D632]/10 text-[#00D632]" : "border-[#27272A] text-[#a1a1aa]"}`}>Coins</button>
                <button onClick={() => setCur("cash")} data-testid="tip-cur-cash"
                  className={`rounded-lg border px-2 py-2 text-xs font-bold ${cur === "cash" ? "border-[#00D632] bg-[#00D632] text-[#0A0A0A]" : "border-[#27272A] text-[#a1a1aa]"}`}>Cash</button>
              </div>
            </div>
          </div>
          <p className="text-xs text-[#a1a1aa]">
            Your {cur}: <b className="text-white">{streamerMode ? "•••" : (cur === "coins" ? `${(user?.coins || 0).toFixed(2)}` : `$${(user?.cash || 0).toFixed(2)}`)}</b>
          </p>
          <Button onClick={tip} disabled={busy} data-testid="send-tip-button"
            className="w-full rounded-full bg-[#00D632] py-6 font-bold text-[#0A0A0A] hover:bg-[#00B829]">
            {busy ? "Sending..." : "Send tip"}
          </Button>
        </div>
      </motion.div>
    </div>
    </div>
  );
}
